import React from 'react';
import { motion } from 'framer-motion';
import './Footer.css';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <footer className="footer">
      <div className="footer-bg"></div>
      
      <div className="container">
        <div className="footer-content">
          <motion.div 
            className="footer-section brand-section"
            variants={fadeInUp}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <div className="footer-logo">
              <span className="logo-text neon-text">IMMOACTIVAT</span>
              <span className="logo-subtitle">I ASSOCIATS SL</span>
            </div>
            
            <p className="footer-description">
              Especialistas en desarrollo de software seguro, ciberseguridad e inteligencia artificial. 
              Transformamos ideas en soluciones digitales innovadoras que protegen y potencian tu negocio.
            </p>

            <div className="security-badge">
              <span className="badge-icon">🛡️</span>
              <span className="badge-text">Privacy & Security First</span>
            </div>
          </motion.div>

          <motion.div 
            className="footer-section"
            variants={fadeInUp}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <h3 className="footer-title">Navegación</h3>
            <ul className="footer-links">
              <li>
                <button onClick={() => scrollToSection('about')} className="footer-link">
                  Sobre Nosotros
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('projects')} className="footer-link">
                  Proyectos
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('services')} className="footer-link">
                  Servicios
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className="footer-link">
                  Contacto
                </button>
              </li>
            </ul>
          </motion.div>

          <motion.div 
            className="footer-section"
            variants={fadeInUp}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <h3 className="footer-title">Servicios</h3>
            <ul className="footer-links">
              <li>
                <span className="footer-service">Desarrollo Móvil</span>
              </li>
              <li>
                <span className="footer-service">Desarrollo Web</span>
              </li>
              <li>
                <span className="footer-service">Auditoría de Seguridad</span>
              </li>
              <li>
                <span className="footer-service">Soluciones de IA/ML</span>
              </li>
              <li>
                <span className="footer-service">Consultoría GDPR</span>
              </li>
            </ul>
          </motion.div>

          <motion.div 
            className="footer-section"
            variants={fadeInUp}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <h3 className="footer-title">Síguenos</h3>
            <div className="social-links">
              <a 
                href="https://www.youtube.com/@greyhatspain"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link youtube"
                aria-label="YouTube"
              >
                <span className="social-icon">📺</span>
                <span>YouTube</span>
              </a>
              
              <a 
                href="https://github.com/R00tedbrain"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link github"
                aria-label="GitHub"
              >
                <span className="social-icon">💻</span>
                <span>GitHub</span>
              </a>
              
              <a 
                href="https://www.fiverr.com/sirhoff/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link fiverr"
                aria-label="Fiverr"
              >
                <span className="social-icon">💼</span>
                <span>Fiverr</span>
              </a>
            </div>

            <div className="contact-info">
              <h4 className="contact-title">Contacto Directo</h4>
              <div className="contact-item">
                <span className="contact-icon">📧</span>
                <span>r00tedbrain@immoactivatiassociatssl.com</span>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📧</span>
                <span>bwtsafedevstests@proton.me</span>
              </div>
              <div className="contact-item">
                <span className="contact-icon">💬</span>
                <a 
                  href="https://wa.me/18255570174"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#00ff41', textDecoration: 'none' }}
                >
                  +1 825 557 0174
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div 
          className="footer-bottom"
          variants={fadeInUp}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
        >
          <div className="footer-divider"></div>
          
          <div className="footer-bottom-content">
            <div className="copyright">
              <p>
                © {currentYear} IMMOACTIVAT I ASSOCIATS SL. Todos los derechos reservados.
              </p>
              <p className="gdpr-notice">
                🔒 Cumplimos estrictamente con el GDPR y protegemos tu privacidad.
              </p>
            </div>

            <div className="footer-actions">
              <button 
                onClick={scrollToTop}
                className="back-to-top"
                aria-label="Volver arriba"
              >
                <span className="arrow-icon">↑</span>
                <span>Volver Arriba</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Matrix effect particles */}
      <div className="footer-particles">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="particle" style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${3 + Math.random() * 4}s`
          }}></div>
        ))}
      </div>
    </footer>
  );
};

export default Footer;